import { Scene, GameObjects } from 'phaser';
import { UnitType } from '../game/config/BalanceConfig';

export class UnitAssignmentUI {
  private scene: Scene;
  private buttons: Map<UnitType, GameObjects.Container> = new Map();
  private buttonLabels: Map<UnitType, GameObjects.Text> = new Map();
  private unitCounters: Map<UnitType, GameObjects.Text> = new Map();
  private availableUnits: number = 0;
  private unitCounts: Map<UnitType, number> = new Map();

  constructor(scene: Scene) {
    this.scene = scene;
    this.initializeUnitCounts();
    this.createUI();
    this.setupEventListeners();
  }

  private initializeUnitCounts(): void {
    this.unitCounts.set(UnitType.TANK, 0);
    this.unitCounts.set(UnitType.RANGED, 0);
    this.unitCounts.set(UnitType.CHARGER, 0);
  }

  private createUI(): void {
    const { width, height } = this.scene.cameras.main;

    // Responsive sizing based on screen width
    const minDimension = Math.min(width, height);
    const buttonWidth = Math.max(70, minDimension * 0.18);
    const buttonHeight = Math.max(40, minDimension * 0.1);
    const fontSize = Math.max(12, minDimension * 0.03);
    const buttonSpacing = width / 4;
    const buttonY = height - buttonHeight - 10; // Position buttons at very bottom with smaller margin

    // Create Tank button
    this.createUnitButton(
      buttonSpacing,
      buttonY,
      'TANK',
      UnitType.TANK,
      '#00ff00',
      buttonWidth,
      buttonHeight,
      fontSize
    );

    // Create Ranged button
    this.createUnitButton(
      buttonSpacing * 2,
      buttonY,
      'RANGED',
      UnitType.RANGED,
      '#00ffff',
      buttonWidth,
      buttonHeight,
      fontSize
    );

    // Create Charger button
    this.createUnitButton(
      buttonSpacing * 3,
      buttonY,
      'CHARGER',
      UnitType.CHARGER,
      '#ff00ff',
      buttonWidth,
      buttonHeight,
      fontSize
    );
  }

  private createUnitButton(
    x: number,
    y: number,
    label: string,
    unitType: UnitType,
    _color: string,
    width: number,
    height: number,
    fontSize: number
  ): void {
    const container = this.scene.add.container(x, y);

    // Button background with border
    const background = this.scene.add.rectangle(0, 0, width, height, 0x111111, 0.9);
    background.setStrokeStyle(2, 0x333333);

    // Button label
    const labelText = this.scene.add.text(0, -height * 0.15, label, {
      fontSize: `${fontSize}px`,
      fontFamily: 'Courier New',
      color: '#444444',
      align: 'center'
    }).setOrigin(0.5);

    // Unit counter
    const counterText = this.scene.add.text(0, height * 0.15, '0', {
      fontSize: `${fontSize * 0.7}px`,
      fontFamily: 'Courier New',
      color: '#666666',
      align: 'center'
    }).setOrigin(0.5);

    container.add([background, labelText, counterText]);

    // Set hit area for better touch detection
    container.setSize(width, height);

    // Simplified interactive setup
    container.setInteractive({ useHandCursor: true });



    // Store references
    this.buttons.set(unitType, container);
    this.buttonLabels.set(unitType, labelText);
    this.unitCounters.set(unitType, counterText);

    // Setup button interactions
    container.on('pointerdown', () => {
      if (this.availableUnits > 0) {
        this.onButtonClicked(unitType);
        background.setFillStyle(0x222222, 0.9);
      }
    });

    container.on('pointerup', () => {
      this.updateButtonStates();
    });

    container.on('pointerover', () => {
      this.onButtonHover(unitType, true);
    });

    container.on('pointerout', () => {
      this.onButtonHover(unitType, false);
    });
  }

  private setupEventListeners(): void {
    this.scene.events.on('unit-available', (count: number) => {
      this.availableUnits = count;
      this.updateButtonStates();
    });

    this.scene.events.on('unit-deployed', (unit: any) => {
      const unitType = unit.getUnitType();
      const currentCount = this.unitCounts.get(unitType) || 0;
      this.unitCounts.set(unitType, currentCount + 1);
      this.updateUnitCounters();
    });

    this.scene.events.on('unit-destroyed', (unit: any) => {
      const unitType = unit.getUnitType();
      const currentCount = this.unitCounts.get(unitType) || 0;
      this.unitCounts.set(unitType, Math.max(0, currentCount - 1));
      this.updateUnitCounters();
    });
  }

  private onButtonClicked(unitType: UnitType): void {
    if (this.availableUnits > 0) {
      this.scene.events.emit('assign-unit', unitType);
    }
  }

  private onButtonHover(unitType: UnitType, isHovering: boolean): void {
    const button = this.buttons.get(unitType);
    if (!button) return;

    const background = button.getAt(0) as GameObjects.Rectangle;

    if (isHovering && this.availableUnits > 0) {
      background.setStrokeStyle(2, 0x00ffff);
    } else {
      background.setStrokeStyle(2, 0x333333);
    }
  }

  private updateButtonStates(): void {
    this.buttons.forEach((button, unitType) => {
      const label = this.buttonLabels.get(unitType);
      const background = button.getAt(0) as GameObjects.Rectangle;

      if (this.availableUnits > 0) {
        label?.setStyle({ color: '#ffffff' });
        background.setFillStyle(0x222222, 0.8);
      } else {
        label?.setStyle({ color: '#444444' });
        background.setFillStyle(0x111111, 0.8);
      }
    });
  }

  private updateUnitCounters(): void {
    this.unitCounters.forEach((counter, unitType) => {
      const count = this.unitCounts.get(unitType) || 0;
      counter.setText(count.toString());
    });
  }

  public setAvailableUnits(count: number): void {
    this.availableUnits = count;
    this.updateButtonStates();
  }

  public updateUnitCount(unitType: UnitType, count: number): void {
    this.unitCounts.set(unitType, count);
    this.updateUnitCounters();
  }

  public getUnitCount(unitType: UnitType): number {
    return this.unitCounts.get(unitType) || 0;
  }

  public destroy(): void {
    this.scene.events.off('unit-available');
    this.scene.events.off('unit-deployed');
    this.scene.events.off('unit-destroyed');

    this.buttons.forEach(button => button.destroy());
    this.buttons.clear();
    this.buttonLabels.clear();
    this.unitCounters.clear();
  }
}
